import { v4 as uuid } from 'uuid'
import { ChartAlertSnapshot, ChartIndicator, UserAlertRequest } from '../components/alerts/alertModal.typings'
import { Exchange, NotificationChannel, Params, TimeBucket, TimeBucket_TimeSeries } from '../proto/generated/common'
import {
    AlertExpression,
    AlertExpression_Type,
    AlertIndicator,
    Condition,
    Condition_CompositeCondition_Operator,
    Condition_SecurityAlertCondition,
    Condition_SecurityAlertCondition_Operator,
    UserAlert,
    UserAlert_Category,
    UserAlert_Status,
    UserAlert_Trigger,
    UserAlert_Type,
    AlertDrawing,
} from '../proto/generated/nexus'
import {
    sanitizeId,
    getIndicatorDisplayNameForAlert,
    IndicatorSeriesGroupLookup,
    indicatorInputValuesSemanticallyEqual,
    decodeIndicatorOutputSource,
    decodeStableIndicatorOutputSource,
    encodeIndicatorOutputSource,
    encodeStableIndicatorOutputSource,
    formatIndicatorOutputSourceLabel,
    formatIndicatorSourceValueForDisplay,
    formatStableIndicatorOutputSourceLabel,
    isAnyIndicatorOutputSourceReference,
    isIndicatorPriceSourceInput,
    resolveIndicatorInputValue,
} from './indicators'
import { Indicator, IndicatorInput, IndicatorOutput_OutputType, Indicator_Format } from '../proto/generated/indicator'
import { operatorDisplayNames } from '../components/alerts/alertOperatorDropdown/alertOperatorDropdown'
import { getSecurityAsync, getSecurityKey } from './security'
import { Security } from '../proto/generated/security'
import {
    FixedExpressionOutputTypes
} from '../components/alerts/alertExpressionDropdown/alertExpressionDropdown.typings'
import { SingleValueData, Time } from 'lightweight-charts'
import { convertToUTC, convertToTimeZone, TimeZone } from './dateTime'
import type { Point } from '../components/chartV2/drawingTools/drawing.interface'
import { darkBackgroundSecondaryTextColor } from '../theme/theme'
import dayjs from 'dayjs'
import { convertKeyValueArrayToMap, convertMapToKeyValueArray } from './common'
import { getTimeBucketDisplayName } from './timeBuckets'
import { VOLUME_INDICATOR_CODE } from '../assets/constants'
import { formatLargeNumber } from './screener'

export type AlertExpressionSelectionContext = {
    chartSnapshot?: ChartAlertSnapshot
    mode?: 'lhs' | 'rhs'
}

export type AlertMessageContext = {
    chartSnapshot?: ChartAlertSnapshot
    chartTimeBucket?: TimeBucket
}

function buildIndicatorSourceLabelResolver(
    chartSnapshot: ChartAlertSnapshot | undefined,
    indicators: Indicator[]
): (value: string) => string {
    if (!chartSnapshot?.indicators?.length) {
        return (value) => formatIndicatorSourceValueForDisplay(value, indicators)
    }
    const seriesGroups = buildSeriesGroupsFromChartSnapshot(chartSnapshot)
    return (value: string) => {
        if (decodeStableIndicatorOutputSource(value)) {
            return formatStableIndicatorOutputSourceLabel(value, indicators)
        }
        const ref = decodeIndicatorOutputSource(value)
        if (ref) {
            const seriesGroup = seriesGroups[ref.seriesGroupId]
            if (seriesGroup) {
                const producer = indicators.find((item) => item.code === seriesGroup.scriptCode)
                const output = producer?.outputs.find((item) => item.code === ref.outputCode)
                const shortName = producer?.shortName || producer?.code || seriesGroup.scriptCode
                return formatIndicatorOutputSourceLabel(shortName, output?.title ?? ref.outputCode)
            }
        }
        return formatIndicatorSourceValueForDisplay(value, indicators)
    }
}

export async function formatSecurityLabelForAlertIndicatorTail(
    sec: Security,
    chartTimeBucket?: TimeBucket
): Promise<string> {
    const security = await getSecurityAsync(getSecurityKey(sec.exchange, sec.code))
    const name = security.symbol?.trim() || security.code
    const exchangeLabel =
        security.exchange !== Exchange.EXCHANGE_UNKNOWN && security.exchange !== Exchange.UNRECOGNIZED
            ? Exchange[security.exchange]
            : ''
    let label = exchangeLabel ? `${name} • ${exchangeLabel}` : name
    if (chartTimeBucket) {
        label += `, ${getTimeBucketDisplayName(chartTimeBucket)}`
    }
    return label
}

async function formatAlertVolumeLhsLabel(
    security: Security | undefined,
    symbolName: string,
    chartTimeBucket?: TimeBucket
): Promise<string> {
    if (security?.code) {
        return `${await formatSecurityLabelForAlertIndicatorTail(security, chartTimeBucket)} Volume`
    }
    const base =
        symbolName.includes(',') || !chartTimeBucket
            ? symbolName
            : `${symbolName}, ${getTimeBucketDisplayName(chartTimeBucket)}`
    return `${base} Volume`
}

async function resolveAlertIndicatorUnderlyingTail(
    condition: Condition_SecurityAlertCondition,
    symbolName: string,
    chartTimeBucket?: TimeBucket
): Promise<string> {
    const lhs = condition.lhsExpression
    if (lhs?.type !== AlertExpression_Type.TECHNICAL_INDICATOR) {
        return symbolName
    }

    const nestedExpression = getDeepestSecurityAlertExpression(lhs.indicator?.expression)
    const nestedSecurity =
        nestedExpression?.simpleSecurity?.security ??
        nestedExpression?.spreadSecurity?.security?.[0]

    if (nestedSecurity?.code) {
        return formatSecurityLabelForAlertIndicatorTail(nestedSecurity as Security, chartTimeBucket)
    }

    if (symbolName.includes(',')) {
        return symbolName
    }
    if (chartTimeBucket) {
        return `${symbolName}, ${getTimeBucketDisplayName(chartTimeBucket)}`
    }
    return symbolName
}

/** Outputs shown in alert UI menus (excludes line/fill-only series). */
export function isIndicatorOutputSelectableForAlertUi(o: { type: IndicatorOutput_OutputType }): boolean {
    return o.type !== IndicatorOutput_OutputType.HZ_LINE && o.type !== IndicatorOutput_OutputType.FILL
}

/** First output code used for new alerts / context menu when defaulting an indicator leg. */
export function firstIndicatorOutputCodeForAlertUi(
    outputs: { type: IndicatorOutput_OutputType; code: string }[] | undefined
): string | undefined {
    if (!outputs?.length) return undefined
    return outputs.find(isIndicatorOutputSelectableForAlertUi)?.code
}

/** Primary chart symbols first, then compare symbols (deduped). */
export function getAlertCompareSecurityTargets(chart: ChartAlertSnapshot): Security[] {
    const out: Security[] = []
    const seen = new Set<string>()
    for (const s of chart.securityList) {
        const k = getSecurityKey(s.exchange, s.code)
        if (seen.has(k)) continue
        seen.add(k)
        out.push(s)
    }
    for (const s of chart.comparableSecurities ?? []) {
        const k = getSecurityKey(s.exchange, s.code)
        if (seen.has(k)) continue
        seen.add(k)
        out.push(s)
    }
    return out
}

/** Top-level `UserAlert.security` (chart context); use when present instead of inferring only from condition. */
export function hasUsableTopLevelAlertSecurity(
    s: { exchange: number; code: string } | undefined
): boolean {
    if (!s || !s.code) return false
    return s.exchange !== Exchange.EXCHANGE_UNKNOWN && s.exchange !== Exchange.UNRECOGNIZED
}

export async function formatSecurityLabelForAlert(sec: Security): Promise<string> {
    const securityKey = getSecurityKey(sec.exchange, sec.code)
    const security = await getSecurityAsync(securityKey)
    const name = security.symbol?.trim() || security.code
    if (security.exchange === Exchange.EXCHANGE_UNKNOWN || security.exchange === Exchange.UNRECOGNIZED) {
        return name
    }
    const ex = Exchange[security.exchange]
    return ex ? `${name} (${ex})` : name
}

export function encodeAlertExpressionTimeBucketKey(tb: TimeBucket): string {
    return `${tb.timeSeries}:${tb.value}`
}

export function decodeAlertExpressionTimeBucketKey(s: string): TimeBucket | undefined {
    const i = s.indexOf(':')
    if (i <= 0) return undefined
    const series = Number(s.slice(0, i))
    const value = Number(s.slice(i + 1))
    if (!Number.isFinite(series) || !Number.isFinite(value)) return undefined
    return { timeSeries: series as TimeBucket_TimeSeries, value }
}

export function formatExpressionTimeBucketSuffix(tb: TimeBucket | undefined): string {
    if (!tb) return ''
    return ` @ ${getTimeBucketDisplayName(tb)}`
}

/** Primary price stream uses 1s aggregation in proto; hide from alert text. */
function shouldShowTimeBucketInAlertMessage(tb: TimeBucket | undefined): boolean {
    return tb !== undefined && !(tb.timeSeries === TimeBucket_TimeSeries.SECOND && tb.value === 1)
}

export const getPrimarySecurityAlertCondition = (
    condition?: Condition
): Condition_SecurityAlertCondition | undefined => {
    if (!condition) return undefined
    if (condition.securityAlertCondition) return condition.securityAlertCondition
    if (condition.compositeCondition?.condition?.length) {
        for (const child of condition.compositeCondition.condition) {
            const found = getPrimarySecurityAlertCondition(child)
            if (found) return found
        }
    }
    return undefined
}

export function getColorForAlertStatus(displayName: string, darkBackground?: boolean): string {
    if (displayName === "Active") {
        return "success.dark"
    } else if (displayName === "Stopped manually"){
        if(darkBackground) return darkBackgroundSecondaryTextColor
        return "text.secondary"
    }else {
        return "warning.light"
    }
}

type AlertStatusPalette = {
    text: { secondary: string }
    success?: { dark?: string; main: string }
    warning?: { light?: string; main: string }
}

/** Resolve alert status color token to a canvas/CSS color (matches alerts tab Typography colors). */
export function resolveColorForAlertStatus(
    displayName: string | undefined,
    palette: AlertStatusPalette,
    darkBackground?: boolean
): string {
    if (!displayName) return palette.text.secondary
    const colorKey = getColorForAlertStatus(displayName, darkBackground)
    if (colorKey === darkBackgroundSecondaryTextColor) return colorKey
    if (colorKey === 'text.secondary') return palette.text.secondary
    if (colorKey === 'success.dark') {
        return palette.success?.dark ?? palette.success?.main ?? palette.text.secondary
    }
    if (colorKey === 'warning.light') {
        return palette.warning?.light ?? palette.warning?.main ?? palette.text.secondary
    }
    return palette.text.secondary
}

/** Status label used for color lookup — prefers server secondaryStatus like the alerts tab. */
export function getChartAlertStatusColorLabel(alert: UserAlert, displayedStatus?: string): string | undefined {
    const secondary = alert.secondaryStatus?.trim()
    if (secondary) return secondary
    return displayedStatus
}

/** Human-readable status for inactive alerts shown on chart price lines. */
export function getChartAlertStatusLabel(alert: UserAlert): string | undefined {
    if (alert.status === UserAlert_Status.ACTIVE) return undefined
    const secondary = alert.secondaryStatus?.trim()
    if (secondary) return secondary
    switch (alert.status) {
        case UserAlert_Status.STOPPED:
            return 'Stopped'
        case UserAlert_Status.INVALID:
            return 'Invalid'
        case UserAlert_Status.ALERT_STATUS_UNKNOWN:
            return 'Unknown'
        default:
            return undefined
    }
}

export const CHART_ALERT_STATUS_SEPARATOR = ' · '

export type ChartAlertLineTagParts = {
    primary: string
    status?: string
}

export function getChartAlertLineTagParts(primaryText: string, alert: UserAlert): ChartAlertLineTagParts {
    const status = getChartAlertStatusLabel(alert)
    return status ? { primary: primaryText, status } : { primary: primaryText }
}

export function formatChartAlertLineTag(primaryText: string, alert: UserAlert): string {
    const { primary, status } = getChartAlertLineTagParts(primaryText, alert)
    return status ? `${primary}${CHART_ALERT_STATUS_SEPARATOR}${status}` : primary
}

/** Truncate only the primary segment; status suffix is always kept intact. */
export function truncateChartAlertLineTagParts(
    parts: ChartAlertLineTagParts,
    maxWidth: number,
    measureText: (text: string) => number
): ChartAlertLineTagParts {
    const { primary, status } = parts
    if (!status) {
        let text = primary
        while (text.length > 1 && measureText(text + '…') > maxWidth) {
            text = text.slice(0, -1)
        }
        if (text.length < primary.length) text += '…'
        return { primary: text }
    }

    const suffix = `${CHART_ALERT_STATUS_SEPARATOR}${status}`
    const suffixWidth = measureText(suffix)
    const availableForPrimary = Math.max(0, maxWidth - suffixWidth)
    let truncatedPrimary = primary
    if (availableForPrimary > 0 && measureText(truncatedPrimary) > availableForPrimary) {
        while (truncatedPrimary.length > 1 && measureText(truncatedPrimary + '…') > availableForPrimary) {
            truncatedPrimary = truncatedPrimary.slice(0, -1)
        }
        if (truncatedPrimary.length < primary.length) truncatedPrimary += '…'
    } else if (availableForPrimary === 0 && truncatedPrimary.length > 0) {
        truncatedPrimary = ''
    }
    return { primary: truncatedPrimary, status }
}

export function measureChartAlertLineTagWidth(
    parts: ChartAlertLineTagParts,
    measureText: (text: string) => number
): number {
    if (!parts.status) return measureText(parts.primary)
    return measureText(`${parts.primary}${CHART_ALERT_STATUS_SEPARATOR}${parts.status}`)
}

/** Truncate only the primary segment; status suffix is always kept intact. */
export function truncateChartAlertLineLabel(
    parts: ChartAlertLineTagParts,
    maxWidth: number,
    measureText: (text: string) => number
): string {
    const truncated = truncateChartAlertLineTagParts(parts, maxWidth, measureText)
    return truncated.status
        ? `${truncated.primary}${CHART_ALERT_STATUS_SEPARATOR}${truncated.status}`
        : truncated.primary
}

export type AlertIndicatorMatchContext = {
    seriesGroups: IndicatorSeriesGroupLookup
    indicators: Indicator[]
}

export type AlertPersistenceContext = AlertIndicatorMatchContext & {
    chartSnapshot?: ChartAlertSnapshot
}

export function buildSeriesGroupsFromChartSnapshot(chartSnapshot: ChartAlertSnapshot): IndicatorSeriesGroupLookup {
    const seriesGroups: IndicatorSeriesGroupLookup = {}
    chartSnapshot.indicators.forEach((indicator) => {
        seriesGroups[indicator.id] = {
            id: indicator.id,
            scriptCode: indicator.code,
            inputValues: indicator.config ?? {},
        }
    })
    return seriesGroups
}

/** Normalize live chart / Redux series groups for indicator-source resolution. */
export function buildIndicatorSeriesGroupLookup(
    seriesGroups: Record<string, { id?: string; scriptCode: string; inputValues?: Record<string, string> }> | undefined
): IndicatorSeriesGroupLookup {
    const lookup: IndicatorSeriesGroupLookup = {}
    if (!seriesGroups) return lookup
    Object.entries(seriesGroups).forEach(([key, sg]) => {
        lookup[key] = {
            id: sg.id ?? key,
            scriptCode: sg.scriptCode,
            inputValues: sg.inputValues ?? {},
        }
    })
    return lookup
}

function findSeriesGroupIdForNestedAlertIndicator(
    nestedIndicator: AlertIndicator,
    lookup: IndicatorSeriesGroupLookup,
    indicators: Indicator[],
    chartSnapshot?: ChartAlertSnapshot
): string | undefined {
    if (!nestedIndicator?.indicatorCode || !Object.keys(lookup).length) return undefined
    const producerFlat = flattenAlertIndicatorParamsForChart(
        nestedIndicator,
        chartSnapshot,
        indicators,
        lookup
    )
    for (const [seriesGroupId, seriesGroup] of Object.entries(lookup)) {
        if (seriesGroup.scriptCode !== nestedIndicator.indicatorCode) continue
        if (
            indicatorInputValuesSemanticallyEqual(
                producerFlat,
                seriesGroup.inputValues,
                lookup,
                indicators
            )
        ) {
            return seriesGroupId
        }
    }
    return undefined
}

/** Match a chart snapshot indicator instance (supports duplicate codes / indicator-on-indicator params). */
export function findChartSnapshotIndicatorForAlertExpression(
    alertIndicator: { indicatorCode: string; params?: Params[]; output?: string; expression?: AlertExpression } | undefined,
    chartSnapshot: ChartAlertSnapshot | undefined,
    indicators: Indicator[]
): ChartIndicator | undefined {
    if (!alertIndicator?.indicatorCode || !chartSnapshot?.indicators?.length) return undefined
    const seriesGroups = buildSeriesGroupsFromChartSnapshot(chartSnapshot)
    const alertParams = flattenAlertIndicatorParamsForChart(
        {
            indicatorCode: alertIndicator.indicatorCode,
            params: alertIndicator.params ?? [],
            output: alertIndicator.output ?? '',
            expression: alertIndicator.expression,
        },
        chartSnapshot,
        indicators,
        seriesGroups
    )
    return chartSnapshot.indicators.find((chartInd) => {
        if (chartInd.code !== alertIndicator.indicatorCode) return false
        return indicatorInputValuesSemanticallyEqual(
            alertParams,
            chartInd.config ?? {},
            seriesGroups,
            indicators
        )
    })
}

function alertIndicatorParamsHaveSourceRefs(params: Params[] | undefined): boolean {
    return Object.values(convertKeyValueArrayToMap(params)).some((value) => isAnyIndicatorOutputSourceReference(String(value ?? '')))
}

function getAlertIndicatorParamValue(paramsMap: Record<string, string>, input: IndicatorInput): string | undefined {
    return resolveIndicatorInputValue(paramsMap, input)
}

function resolveProducerFromIndicatorSourceValue(
    value: string,
    seriesGroups: IndicatorSeriesGroupLookup,
    indicators: Indicator[]
): { scriptCode: string; inputValues: Record<string, string>; outputCode: string } | null {
    const ephemeral = decodeIndicatorOutputSource(value)
    if (ephemeral) {
        const seriesGroup = seriesGroups[ephemeral.seriesGroupId]
        if (!seriesGroup) return null
        return {
            scriptCode: seriesGroup.scriptCode,
            inputValues: seriesGroup.inputValues,
            outputCode: ephemeral.outputCode,
        }
    }

    const stable = decodeStableIndicatorOutputSource(value)
    if (!stable) return null

    for (const seriesGroup of Object.values(seriesGroups)) {
        if (seriesGroup.scriptCode !== stable.indicatorCode) continue
        if (
            indicatorInputValuesSemanticallyEqual(
                stable.params,
                seriesGroup.inputValues,
                seriesGroups,
                indicators
            )
        ) {
            return {
                scriptCode: seriesGroup.scriptCode,
                inputValues: seriesGroup.inputValues,
                outputCode: stable.outputCode,
            }
        }
    }

    return {
        scriptCode: stable.indicatorCode,
        inputValues: stable.params,
        outputCode: stable.outputCode,
    }
}

function applyPriceSourceToAlertExpression(base: AlertExpression, source: string): AlertExpression {
    if (base.type === AlertExpression_Type.SIMPLE_SECURITY && base.simpleSecurity) {
        return {
            ...base,
            simpleSecurity: {
                ...base.simpleSecurity,
                source,
            },
        }
    }
    if (base.type === AlertExpression_Type.SPREAD_SECURITY && base.spreadSecurity) {
        return {
            ...base,
            spreadSecurity: {
                ...base.spreadSecurity,
                source,
            },
        }
    }
    return base
}

function getBaseSecurityExpressionForAlertIndicator(
    alertIndicator: AlertIndicator,
    fallback?: AlertExpression
): AlertExpression {
    const candidate = alertIndicator.expression ?? fallback
    if (
        candidate?.type === AlertExpression_Type.SIMPLE_SECURITY ||
        candidate?.type === AlertExpression_Type.SPREAD_SECURITY
    ) {
        return candidate
    }
    return (
        fallback ?? {
            type: AlertExpression_Type.SIMPLE_SECURITY,
            simpleSecurity: {
                security: undefined,
                source: 'close',
            },
        }
    )
}

/** Build proto-correct nested AlertIndicator.expression chain for indicator-on-indicator sources. */
function buildNestedAlertIndicator(
    indicatorCode: string,
    paramsMap: Record<string, string>,
    output: string,
    baseSecurityExpression: AlertExpression,
    seriesGroups: IndicatorSeriesGroupLookup,
    indicators: Indicator[]
): AlertIndicator {
    const indicatorDef = indicators.find((item) => item.code === indicatorCode)
    if (!indicatorDef) {
        return {
            indicatorCode,
            params: convertMapToKeyValueArray(paramsMap),
            output,
            expression: baseSecurityExpression,
        }
    }

    const sourceInput = indicatorDef.inputs.find(isIndicatorPriceSourceInput)
    const sourceValue = sourceInput ? getAlertIndicatorParamValue(paramsMap, sourceInput) : undefined
    let sourceExpression = baseSecurityExpression

    const apiParams: Record<string, string> = {}
    indicatorDef.inputs.forEach((input) => {
        const value = getAlertIndicatorParamValue(paramsMap, input)
        if (value === undefined || value === '') return
        if (sourceInput && input.code === sourceInput.code && sourceValue && isAnyIndicatorOutputSourceReference(sourceValue)) {
            return
        }
        apiParams[sanitizeId(input.code)] = value
    })

    if (sourceValue && isAnyIndicatorOutputSourceReference(sourceValue)) {
        const producer = resolveProducerFromIndicatorSourceValue(sourceValue, seriesGroups, indicators)
        if (producer) {
            const nested = buildNestedAlertIndicator(
                producer.scriptCode,
                producer.inputValues,
                producer.outputCode,
                baseSecurityExpression,
                seriesGroups,
                indicators
            )
            sourceExpression = {
                type: AlertExpression_Type.TECHNICAL_INDICATOR,
                indicator: nested,
                timeBucket: undefined,
                simpleSecurity: undefined,
                spreadSecurity: undefined,
                fixedValue: undefined,
            }
        }
    } else if (sourceValue) {
        sourceExpression = applyPriceSourceToAlertExpression(baseSecurityExpression, sourceValue)
    }

    return {
        indicatorCode,
        params: convertMapToKeyValueArray(apiParams),
        output,
        expression: sourceExpression,
    }
}

/** Expand API nested indicator.expression back into chart-style params for UI matching. */
export function flattenAlertIndicatorParamsForChart(
    alertIndicator: AlertIndicator,
    chartSnapshot: ChartAlertSnapshot | undefined,
    indicators: Indicator[],
    seriesGroups?: IndicatorSeriesGroupLookup
): Record<string, string> {
    const indicatorDef = indicators.find((item) => item.code === alertIndicator.indicatorCode)
    const params = convertKeyValueArrayToMap(alertIndicator.params)
    if (!indicatorDef) return params

    const sourceInput = indicatorDef.inputs.find(isIndicatorPriceSourceInput)
    if (!sourceInput) return params

    const nested = alertIndicator.expression

    // UI state stores @ind:/@indRef: in params while expression is a placeholder security row.
    const sourceFromParams = resolveIndicatorInputValue(params, sourceInput)
    if (sourceFromParams && isAnyIndicatorOutputSourceReference(sourceFromParams)) {
        return params
    }
    // Plain price source in params — keep unless a nested indicator chain must be expanded from expression.
    if (sourceFromParams && nested?.type !== AlertExpression_Type.TECHNICAL_INDICATOR) {
        return params
    }

    if (nested?.type === AlertExpression_Type.TECHNICAL_INDICATOR && nested.indicator) {
        const lookup = Object.keys(seriesGroups ?? {}).length
            ? buildIndicatorSeriesGroupLookup(
                  seriesGroups as Record<string, { id?: string; scriptCode: string; inputValues?: Record<string, string> }>
              )
            : chartSnapshot
              ? buildSeriesGroupsFromChartSnapshot(chartSnapshot)
              : {}

        const matchedSeriesGroupId = findSeriesGroupIdForNestedAlertIndicator(
            nested.indicator,
            lookup,
            indicators,
            chartSnapshot
        )
        if (matchedSeriesGroupId) {
            params[sourceInput.code] = encodeIndicatorOutputSource({
                seriesGroupId: matchedSeriesGroupId,
                outputCode: nested.indicator.output,
            })
            return params
        }

        const matched = findChartSnapshotIndicatorForAlertExpression(nested.indicator, chartSnapshot, indicators)
        if (matched) {
            params[sourceInput.code] = encodeIndicatorOutputSource({
                seriesGroupId: matched.id,
                outputCode: nested.indicator.output,
            })
        } else {
            params[sourceInput.code] = encodeStableIndicatorOutputSource({
                indicatorCode: nested.indicator.indicatorCode,
                outputCode: nested.indicator.output,
                params: flattenAlertIndicatorParamsForChart(nested.indicator, chartSnapshot, indicators, lookup),
            })
        }
        return params
    }

    if (nested?.type === AlertExpression_Type.SIMPLE_SECURITY) {
        params[sourceInput.code] = nested.simpleSecurity?.source || 'close'
    } else if (nested?.type === AlertExpression_Type.SPREAD_SECURITY) {
        params[sourceInput.code] = nested.spreadSecurity?.source || 'close'
    }

    return params
}

function normalizeAlertExpressionForApi(
    expression: AlertExpression | undefined,
    seriesGroups: IndicatorSeriesGroupLookup,
    indicators: Indicator[]
): AlertExpression | undefined {
    if (!expression?.indicator) return expression

    const current = expression.indicator
    if (!alertIndicatorParamsHaveSourceRefs(current.params) && current.expression) {
        return expression
    }

    const baseSecurityExpression = getBaseSecurityExpressionForAlertIndicator(current, current.expression)
    const nested = buildNestedAlertIndicator(
        current.indicatorCode,
        convertKeyValueArrayToMap(current.params),
        current.output,
        baseSecurityExpression,
        seriesGroups,
        indicators
    )

    return {
        ...expression,
        type: AlertExpression_Type.TECHNICAL_INDICATOR,
        indicator: nested,
        simpleSecurity: undefined,
        spreadSecurity: undefined,
        fixedValue: undefined,
    }
}

function getDeepestSecurityAlertExpression(expression: AlertExpression | undefined): AlertExpression | undefined {
    if (!expression) return undefined
    if (expression.type === AlertExpression_Type.TECHNICAL_INDICATOR && expression.indicator?.expression) {
        return getDeepestSecurityAlertExpression(expression.indicator.expression)
    }
    return expression
}

export function getChartIndicatorOutputValue(
    chartIndicator: ChartIndicator | undefined,
    outputCode: string | undefined,
    indicators: Indicator[]
): number {
    if (!chartIndicator || !outputCode) return 0
    const def = indicators.find((item) => item.code === chartIndicator.code)
    const outputTitle = def?.outputs?.find((output) => output.code === outputCode)?.title
    if (!outputTitle) return 0
    const raw = chartIndicator.outputValues[outputTitle]?.value
    if (raw === undefined || raw === null || Number.isNaN(Number(raw))) return 0
    return Number(raw)
}

/** Round indicator live values for alert RHS prefills (decimal places, not significant digits). */
export function formatChartIndicatorCompareValue(value: number, indicator?: Indicator): number {
    if (!Number.isFinite(value)) return 0

    if (indicator?.format === Indicator_Format.VOLUME) {
        return Math.round(value)
    }

    const factor = 100
    return Math.round(value * factor) / factor
}

export function formatAlertIndicatorCompareValue(
    value: number,
    indicatorCode: string | undefined,
    indicators: Indicator[]
): number {
    const indicator = indicators.find((item) => item.code === indicatorCode)
    return formatChartIndicatorCompareValue(value, indicator)
}

/** Convert chart indicator-source refs into nested AlertIndicator.expression before persisting alerts. */
export function normalizeAlertForPersistence(
    alert: UserAlert,
    ctx?: AlertPersistenceContext
): UserAlert {
    const seriesGroups =
        ctx?.seriesGroups ??
        (ctx?.chartSnapshot ? buildSeriesGroupsFromChartSnapshot(ctx.chartSnapshot) : undefined)
    const indicators = ctx?.indicators
    if (!seriesGroups || !indicators?.length || !alert.condition?.securityAlertCondition) {
        return alert
    }

    const sac = alert.condition.securityAlertCondition
    const lhsExpression = normalizeAlertExpressionForApi(sac.lhsExpression, seriesGroups, indicators)
    const rhsExpression = normalizeAlertExpressionForApi(sac.rhsExpression, seriesGroups, indicators)

    if (lhsExpression === sac.lhsExpression && rhsExpression === sac.rhsExpression) {
        return alert
    }

    return {
        ...alert,
        condition: {
            ...alert.condition,
            securityAlertCondition: {
                ...sac,
                lhsExpression,
                rhsExpression,
            },
        },
    }
}

export const createUserAlertFromSnapshot = (chartSnapshot: ChartAlertSnapshot): UserAlert => {
    const { timeBucket, expression, expressionMode, securityList, data, chartType } = chartSnapshot;

    const operator = Condition_SecurityAlertCondition_Operator.CROSSING;

    const lhsExpression: AlertExpression = {
        type: expressionMode ? AlertExpression_Type.SPREAD_SECURITY: AlertExpression_Type.SIMPLE_SECURITY,
        timeBucket: {
            timeSeries: TimeBucket_TimeSeries.SECOND,
            value: 1
        }
    }

    if (expressionMode) {
        lhsExpression.spreadSecurity = {
            spreadExpression: expression,
            security: securityList.map(sec => ({exchange: sec.exchange, code: sec.code})),
            source: getOperatorSource(operator)
        }
    } else {
        lhsExpression.simpleSecurity = {
            security: {exchange: securityList[0].exchange, code: securityList[0].code},
            source: getOperatorSource(operator)
        }
    }

    const rhsExpression: AlertExpression = {
        type: AlertExpression_Type.FIXED_VALUE,
        fixedValue: data.close,
        timeBucket: undefined
    }

    const condition: Condition_SecurityAlertCondition = {
        lhsExpression,
        rhsExpression,
        operator
    }

    return {
        id: uuid(),
        chartCandleType: chartType,
        category: UserAlert_Category.USER_ALERT,
        condition: {
            securityAlertCondition: condition
        },
        created: Date.now(),
        expiry: dayjs().add(1, 'M').set('seconds', 0).set('milliseconds', 0).toDate().getTime(),
        lastTriggered: 0,
        message: '',
        metadata: '',
        name: '',
        notificationChannels: [NotificationChannel.DESKTOP_POPUP],
        secondaryStatus: '',
        status: UserAlert_Status.ALERT_STATUS_UNKNOWN,
        timeBucket,
        trigger: UserAlert_Trigger.ONCE,
        type: UserAlert_Type.STANDARD,
        updated: Date.now(),
        allocatedNodes: [],
        userId: 'a0c8f989-3591-4dc8-ac5f-5c4363c7da1f',
        security: securityList[0]
            ? { exchange: securityList[0].exchange, code: securityList[0].code }
            : undefined
    }
}

export const createSnapshotFromUserAlert = (alert: UserAlert, indicators: Indicator[]): ChartAlertSnapshot => {
    const [securityList, expression] = getUserAlertUnderlyingSecurity(alert);
    const chartSnapshot: ChartAlertSnapshot = {
        chartType: alert.chartCandleType,
        data: {
            open: 0,
            high: 0,
            low: 0,
            close: 0,
            time: 0 as Time,
            customValues: {
                volume: 0
            }
        },
        expression,
        expressionMode: !!expression.length,
        indicators: [],
        securityList,
        comparableSecurities: [],
        availableTimeBuckets: [],
        timeBucket: alert.timeBucket!
    }

    const securityAlertCondition = getPrimarySecurityAlertCondition(alert.condition);
    if (securityAlertCondition) {
        const { lhsExpression, rhsExpression } = securityAlertCondition
        const source = (lhsExpression?.simpleSecurity || lhsExpression?.spreadSecurity)?.source ?? "";

        if (lhsExpression?.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            const indicatorConfig = lhsExpression.indicator;
            const indicator = indicators.find(i => i.code === indicatorConfig?.indicatorCode)
            if (indicatorConfig && indicator) {
                const indicatorParamsMap = flattenAlertIndicatorParamsForChart(
                    indicatorConfig as AlertIndicator,
                    chartSnapshot,
                    indicators
                )
                chartSnapshot.indicators.push({
                    code: indicatorConfig.indicatorCode,
                    config: indicatorParamsMap,
                    outputs: indicator.outputs,
                    displayName: getIndicatorDisplayNameForAlert(indicator, indicatorParamsMap, indicators),
                    id: uuid(),
                    inputs: indicator.inputs,
                    name: indicator.name,
                    outputValues: indicator.outputs.reduce((acc, v) => {
                        acc[v.code] = {
                            value: Number(indicatorParamsMap[v.code]) || 0,
                            time: 0 as Time
                        };
                        return acc;
                    }, {} as Record<string, SingleValueData<Time>>),
                    shortName: indicator.shortName

                })
            }
        }

        if (rhsExpression?.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            const indicatorConfig = rhsExpression.indicator;
            const indicator = indicators.find(i => i.code === indicatorConfig?.indicatorCode)
            if (indicatorConfig && indicator && !chartSnapshot.indicators.find((cI) => cI.code === indicatorConfig.indicatorCode)) {
                const indicatorParamsMap = flattenAlertIndicatorParamsForChart(
                    indicatorConfig as AlertIndicator,
                    chartSnapshot,
                    indicators
                )
                chartSnapshot.indicators.push({
                    code: indicatorConfig.indicatorCode,
                    config: indicatorParamsMap,
                    outputs: indicator.outputs,
                    displayName: getIndicatorDisplayNameForAlert(indicator, indicatorParamsMap, indicators),
                    id: uuid(),
                    inputs: indicator.inputs,
                    name: indicator.name,
                    outputValues: indicator.outputs.reduce((acc, v) => {
                        acc[v.code] = {
                            value: Number(indicatorParamsMap[v.code]) || 0,
                            time: 0 as Time
                        };
                        return acc;
                    }, {} as Record<string, SingleValueData<Time>>),
                    shortName: indicator.shortName

                })
            }
        }

        if (["close", "high", "low"].includes(source) &&
            rhsExpression?.type === AlertExpression_Type.FIXED_VALUE &&
            rhsExpression.fixedValue !== undefined) {
            chartSnapshot.data.open = rhsExpression.fixedValue;
            chartSnapshot.data.high = rhsExpression.fixedValue;
            chartSnapshot.data.low = rhsExpression.fixedValue;
            chartSnapshot.data.close = rhsExpression.fixedValue;
        }
    }

    return chartSnapshot;
}

export const getCreateUserAlertRequestObject = (
    alert: UserAlert,
    ctx?: AlertPersistenceContext
): UserAlertRequest => {
    const requestAlert: any = { ...normalizeAlertForPersistence(alert, ctx) };
    delete requestAlert.allocatedNodes;
    delete requestAlert.userId;
    delete requestAlert.created;
    delete requestAlert.secondaryStatus;
    delete requestAlert.lastTriggered;
    delete requestAlert.updated;
    delete requestAlert.status;
    const {condition} = requestAlert as UserAlertRequest
    if (condition && condition.securityAlertCondition) {
        const {lhsExpression, rhsExpression} = condition.securityAlertCondition;
        const newsecurityAlertCondition = {...condition.securityAlertCondition}
        if (lhsExpression?.indicator) {
            const newLhsExpression: AlertExpression = {...lhsExpression};
            const newParams: Record<string, string> = {}
            const indicatorParamsMap = convertKeyValueArrayToMap(lhsExpression.indicator.params);
            for (let input in indicatorParamsMap) {
                newParams[sanitizeId(input)] = indicatorParamsMap[input]
            }
            newLhsExpression.indicator = {
                ...lhsExpression.indicator,
                params: convertMapToKeyValueArray(newParams)
            }
            newsecurityAlertCondition.lhsExpression = newLhsExpression;
        }
        
        if (rhsExpression?.indicator) {
            const newRhsExpression = {...rhsExpression};
            const newParams: Record<string, string> = {}
            const indicatorParamsMap = convertKeyValueArrayToMap(rhsExpression.indicator.params);
            for (let input in indicatorParamsMap) {
                newParams[sanitizeId(input)] = indicatorParamsMap[input]
            }
            newRhsExpression.indicator = {
                ...rhsExpression.indicator,
                params: convertMapToKeyValueArray(newParams)
            }
            newsecurityAlertCondition.rhsExpression = newRhsExpression;
        }

        requestAlert.condition = {
            ...condition,
            securityAlertCondition: newsecurityAlertCondition
        }

    }
    return requestAlert as UserAlertRequest;

}

export const getSelectedValue = (
    expression: AlertExpression,
    indicators: Indicator[],
    ctx?: AlertExpressionSelectionContext
): Array<string> => {
    const { type, simpleSecurity, indicator: alertIndicator, spreadSecurity } = expression

    if (type === AlertExpression_Type.FIXED_VALUE) return [FixedExpressionOutputTypes.VALUE]

    if (type === AlertExpression_Type.DRAWING) return [FixedExpressionOutputTypes.DRAWING]

    if (type === AlertExpression_Type.SIMPLE_SECURITY && simpleSecurity?.security) {
        if (simpleSecurity.source === FixedExpressionOutputTypes.VOLUME.toString()) {
            return [FixedExpressionOutputTypes.VOLUME]
        }
        const snap = ctx?.chartSnapshot
        const mode = ctx?.mode
        if (snap && mode) {
            const isPrimaryLhs =
                mode === 'lhs' &&
                !snap.expressionMode &&
                snap.securityList[0] &&
                snap.securityList[0].exchange === simpleSecurity.security.exchange &&
                snap.securityList[0].code === simpleSecurity.security.code
            if (mode === 'rhs' || !isPrimaryLhs) {
                return [
                    FixedExpressionOutputTypes.COMPARE_SECURITY,
                    getSecurityKey(simpleSecurity.security.exchange, simpleSecurity.security.code),
                    simpleSecurity.source || 'close'
                ]
            }
            return [FixedExpressionOutputTypes.PRICE]
        }
        return [FixedExpressionOutputTypes.PRICE]
    }

    if (type === AlertExpression_Type.SPREAD_SECURITY && spreadSecurity) {
        switch (spreadSecurity.source) {
            case FixedExpressionOutputTypes.VOLUME.toString():
                return [FixedExpressionOutputTypes.VOLUME]
            default:
                return [FixedExpressionOutputTypes.PRICE]
        }
    }

    if (alertIndicator) {
        const snap = ctx?.chartSnapshot
        if (snap?.indicators?.length) {
            const seriesGroups = buildSeriesGroupsFromChartSnapshot(snap)
            const matched = findChartSnapshotIndicatorForAlertExpression(alertIndicator, snap, indicators)
            if (matched) {
                return [matched.id, alertIndicator.output]
            }
            const directParams = convertKeyValueArrayToMap(alertIndicator.params)
            const directMatch = snap.indicators.find((chartInd) => {
                if (chartInd.code !== alertIndicator.indicatorCode) return false
                return indicatorInputValuesSemanticallyEqual(
                    directParams,
                    chartInd.config ?? {},
                    seriesGroups,
                    indicators
                )
            })
            if (directMatch) {
                return [directMatch.id, alertIndicator.output]
            }
        }
        const indicator = indicators.find((v) => v.code === alertIndicator.indicatorCode)
        if (indicator) {
            return [indicator.code, alertIndicator.output]
        }
        if (alertIndicator.indicatorCode) {
            return [alertIndicator.indicatorCode, alertIndicator.output]
        }
        return []
    }
    return []
}

/** True when this side is a non-primary chart symbol (compare) with OHLC source — not Price / Value / Volume / indicator. */
export function isAlertExpressionCompareSecurity(
    expression: AlertExpression | undefined,
    indicators: Indicator[],
    chartSnapshot: ChartAlertSnapshot,
    mode: 'lhs' | 'rhs'
): boolean {
    if (!expression) return false
    return (
        expression.type === AlertExpression_Type.SIMPLE_SECURITY &&
        !!expression.simpleSecurity?.security &&
        expression.simpleSecurity.source !== FixedExpressionOutputTypes.VOLUME.toString() &&
        getSelectedValue(expression, indicators, { chartSnapshot, mode })[0] === FixedExpressionOutputTypes.COMPARE_SECURITY
    )
}

export async function getUserAlertMessage(
    condition: Condition_SecurityAlertCondition,
    symbolName: string,
    indicators: Indicator[],
    ctx?: AlertMessageContext
): Promise<string> {
    const { lhsExpression, operator, rhsExpression } = condition
    if (!lhsExpression || !operator || !rhsExpression) return ''
    const resolveSourceLabel = buildIndicatorSourceLabelResolver(ctx?.chartSnapshot, indicators)
    const chartTimeBucket = ctx?.chartTimeBucket ?? ctx?.chartSnapshot?.timeBucket
    const indicatorUnderlyingTail = await resolveAlertIndicatorUnderlyingTail(
        condition,
        symbolName,
        chartTimeBucket
    )
    let message = ''

    switch (lhsExpression.type) {
        case AlertExpression_Type.SIMPLE_SECURITY: {
            const s = lhsExpression.simpleSecurity?.security
            const isVolume = isVolumeLhsSource(lhsExpression.simpleSecurity?.source)
            if (isVolume) {
                message += await formatAlertVolumeLhsLabel(s as Security | undefined, symbolName, chartTimeBucket)
            } else if (s) {
                message += await formatSecurityLabelForAlert(s)
            } else {
                message += symbolName
            }
            if (!isVolume && shouldShowTimeBucketInAlertMessage(lhsExpression.timeBucket)) {
                message += formatExpressionTimeBucketSuffix(lhsExpression.timeBucket)
            }
            break
        }
        case AlertExpression_Type.SPREAD_SECURITY:
            if (isVolumeLhsSource(lhsExpression.spreadSecurity?.source)) {
                message += await formatAlertVolumeLhsLabel(
                    lhsExpression.spreadSecurity?.security?.[0] as Security | undefined,
                    symbolName,
                    chartTimeBucket
                )
            } else {
                message += symbolName
            }
            break
        case AlertExpression_Type.TECHNICAL_INDICATOR:
            const indicator = indicators.find(i => i.code === lhsExpression.indicator!.indicatorCode)
            if (!indicator) return '';
            const flatParams = flattenAlertIndicatorParamsForChart(
                lhsExpression.indicator as AlertIndicator,
                ctx?.chartSnapshot,
                indicators
            )
            message += (' ' + getIndicatorDisplayNameForAlert(
                indicator,
                flatParams,
                indicators,
                resolveSourceLabel
            ));
            break;
    }

    message += (' ' + operatorDisplayNames[operator])

    const lhsIsVolumeCondition =
        (lhsExpression.type === AlertExpression_Type.SIMPLE_SECURITY && isVolumeLhsSource(lhsExpression.simpleSecurity?.source)) ||
        (lhsExpression.type === AlertExpression_Type.SPREAD_SECURITY && isVolumeLhsSource(lhsExpression.spreadSecurity?.source))

    switch (rhsExpression.type) {
        case AlertExpression_Type.FIXED_VALUE:
            message += ' ' + (lhsIsVolumeCondition ? formatLargeNumber(rhsExpression.fixedValue ?? 0) : rhsExpression.fixedValue!)
            break
        case AlertExpression_Type.DRAWING: {
            const drawingType = rhsExpression.drawing?.drawingType
            message += drawingType ? ` ${formatDrawingTypeForAlertMessage(drawingType)}` : ' drawing'
            break
        }
        case AlertExpression_Type.SIMPLE_SECURITY: {
            const rs = rhsExpression.simpleSecurity
            if (rs?.security) {
                const src = rs.source ? ` ${rs.source}` : ''
                message += ` ${await formatSecurityLabelForAlert(rs.security)}${src}`
                if (shouldShowTimeBucketInAlertMessage(rhsExpression.timeBucket)) {
                    message += formatExpressionTimeBucketSuffix(rhsExpression.timeBucket)
                }
            }
            break
        }
        case AlertExpression_Type.TECHNICAL_INDICATOR:
            const rhsIndicator = indicators.find(i => i.code === rhsExpression.indicator!.indicatorCode)
            if (!rhsIndicator) return '';
            const rhsFlatParams = flattenAlertIndicatorParamsForChart(
                rhsExpression.indicator as AlertIndicator,
                ctx?.chartSnapshot,
                indicators
            )
            message += (' ' + getIndicatorDisplayNameForAlert(
                rhsIndicator,
                rhsFlatParams,
                indicators,
                resolveSourceLabel
            ));
            break;
    }

    if (lhsExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
        message += ` on ${indicatorUnderlyingTail}`
    }

    return message.trim()
}

function compositeMessageSeparator(op: Condition_CompositeCondition_Operator | undefined): string {
    if (op === Condition_CompositeCondition_Operator.OR) return ' OR '
    return ' AND '
}

/**
 * Wrap a nested composite in parentheses only when needed:
 * - more than one direct proto child (real sibling group), or
 * - one child but the text mixes AND/OR with the parent (precedence).
 * Single-child wrapper composites (redundant grouping) stay unwrapped.
 */
function compositeOperandNeedsParentheses(
    child: Condition,
    parentSeparator: string,
    formattedChild: string
): boolean {
    if (!child.compositeCondition) return false
    const sub = child.compositeCondition.condition ?? []
    if (sub.length > 1) return true
    if (sub.length === 0) return false
    const parentIsAnd = parentSeparator === ' AND '
    if (parentIsAnd && formattedChild.includes(' OR ')) return true
    if (!parentIsAnd && formattedChild.includes(' AND ')) return true
    return false
}

/** User-visible alert text for any condition shape; nested composite groups get ( ) only when needed. */
export async function getUserAlertMessageFromCondition(
    condition: Condition | undefined,
    symbolName: string,
    indicators: Indicator[],
    ctx?: AlertMessageContext
): Promise<string> {
    if (!condition) return ''
    if (condition.securityAlertCondition) {
        return getUserAlertMessage(condition.securityAlertCondition, symbolName, indicators, ctx)
    }
    const comp = condition.compositeCondition
    const children = comp?.condition ?? []
    if (!children.length) return ''
    const sep = compositeMessageSeparator(comp?.logicalOperator)
    const parts: string[] = []
    for (const child of children) {
        const s = await getUserAlertMessageFromCondition(child, symbolName, indicators, ctx)
        if (!s) continue
        if (compositeOperandNeedsParentheses(child, sep, s)) {
            parts.push(`(${s})`)
        } else {
            parts.push(s)
        }
    }
    if (parts.length === 0) return ''
    if (parts.length === 1) return parts[0]
    return parts.join(sep)
}

export const getUserAlertKey = (alert: UserAlert) => {
    const [securityList, expression] = getUserAlertUnderlyingSecurity(alert) || [];

    return expression || (securityList.length && getSecurityKey(securityList[0].exchange, securityList[0].code)) || ''
}

export const getUserAlertUnderlyingSecurity = (alert: UserAlert): [Security[], string] => {
    const defaultResult: [Security[], string] = [[], '']
    const top = alert.security

    if (!alert.condition) {
        if (hasUsableTopLevelAlertSecurity(top)) return [[top as Security], '']
        return defaultResult
    }
    const sac = getPrimarySecurityAlertCondition(alert.condition)
    if (!sac?.lhsExpression) {
        if (hasUsableTopLevelAlertSecurity(top)) return [[top as Security], '']
        return defaultResult
    }
    const { lhsExpression } = sac
    if (!lhsExpression) {
        if (hasUsableTopLevelAlertSecurity(top)) return [[top as Security], '']
        return defaultResult
    }
    const { simpleSecurity, spreadSecurity, indicator } = lhsExpression

    let fromCondition: [Security[], string] = defaultResult
    switch (lhsExpression.type) {
        case AlertExpression_Type.SIMPLE_SECURITY:
            if (!simpleSecurity || !simpleSecurity.security) {
                fromCondition = defaultResult
                break
            }
            fromCondition = [[simpleSecurity.security], '']
            break
        case AlertExpression_Type.SPREAD_SECURITY:
            if (!spreadSecurity || !spreadSecurity.spreadExpression) {
                fromCondition = defaultResult
                break
            }
            fromCondition = [spreadSecurity.security, spreadSecurity.spreadExpression]
            break
        case AlertExpression_Type.TECHNICAL_INDICATOR:
            if (!indicator) {
                fromCondition = defaultResult
                break
            }
            const deepestExpression = getDeepestSecurityAlertExpression(indicator.expression)
            if (!deepestExpression) {
                fromCondition = defaultResult
                break
            }
            switch (deepestExpression.type) {
                case AlertExpression_Type.SIMPLE_SECURITY:
                    if (!deepestExpression.simpleSecurity || !deepestExpression.simpleSecurity.security) {
                        fromCondition = defaultResult
                        break
                    }
                    fromCondition = [[deepestExpression.simpleSecurity.security], '']
                    break
                case AlertExpression_Type.SPREAD_SECURITY:
                    if (!deepestExpression.spreadSecurity || !deepestExpression.spreadSecurity.spreadExpression) {
                        fromCondition = defaultResult
                        break
                    }
                    fromCondition = [deepestExpression.spreadSecurity.security, deepestExpression.spreadSecurity.spreadExpression]
                    break
                default:
                    fromCondition = defaultResult
                    break
            }
            break
    }

    const [, expressionStr] = fromCondition
    if (!expressionStr.length && hasUsableTopLevelAlertSecurity(top)) {
        return [[top as Security], '']
    }
    return fromCondition
}

export const getOperatorSource = (operator: Condition_SecurityAlertCondition_Operator): string => {
    switch (operator) {
        case Condition_SecurityAlertCondition_Operator.EQUAL_TO:
            return 'close'
        case Condition_SecurityAlertCondition_Operator.CROSSING_UP:
        case Condition_SecurityAlertCondition_Operator.CROSSING:
        case Condition_SecurityAlertCondition_Operator.GREATER_THAN:
            return 'high'
        case Condition_SecurityAlertCondition_Operator.CROSSING_DOWN:
        case Condition_SecurityAlertCondition_Operator.LESS_THAN:
            return 'low'
        default:
            return 'close'
    }
}

/** Convert chart time (seconds, chart timezone) to candle startTimestamp milliseconds for alert storage. */
export function chartTimeToAlertTimestampMs(time: Time, timeZone: TimeZone): number {
    if (typeof time !== 'number') return 0
    return convertToUTC(time, timeZone) * 1000
}

/**
 * Drawing types that support "Add alert on drawing" (TradingView line tools).
 * Channels, rectangles, Fib, and Anchored VWAP need zone/channel operators not
 * yet supported by the alert engine — keep those off this list until then.
 */
export const DRAWING_TYPES_SUPPORTING_ALERTS = [
    'trendLine',
    'arrow',
    'ray',
    'extendedLine',
    'trendAngle',
    'infoLine',
    'horizontalLine',
    'horizontalRay',
] as const

export type AlertableDrawingType = (typeof DRAWING_TYPES_SUPPORTING_ALERTS)[number]

export function isAlertableDrawingType(drawingType: string | undefined | null): drawingType is AlertableDrawingType {
    return Boolean(drawingType && (DRAWING_TYPES_SUPPORTING_ALERTS as readonly string[]).includes(drawingType))
}

export function isHorizontalAlertDrawingType(drawingType: string | undefined | null): boolean {
    return drawingType === 'horizontalLine' || drawingType === 'horizontalRay'
}

/** Human-readable label for a chart drawing type in alert messages. */
export function formatDrawingTypeForAlertMessage(drawingType: string): string {
    switch (drawingType) {
        case 'trendLine':
            return 'trendline'
        case 'arrow':
            return 'arrow'
        case 'horizontalLine':
            return 'horizontal line'
        case 'horizontalRay':
            return 'horizontal ray'
        case 'ray':
            return 'ray'
        case 'extendedLine':
            return 'extended line'
        case 'trendAngle':
            return 'trend angle'
        case 'infoLine':
            return 'info line'
        default:
            return drawingType.replace(/([A-Z])/g, ' $1').trim().toLowerCase()
    }
}

export function getDrawingAlertDisplayLabel(drawingType: string | undefined | null): string {
    if (!drawingType) return 'Drawing'
    switch (drawingType) {
        case 'trendLine':
            return 'Trend Line'
        case 'arrow':
            return 'Arrow'
        case 'horizontalLine':
            return 'Horizontal Line'
        case 'horizontalRay':
            return 'Horizontal Ray'
        case 'ray':
            return 'Ray'
        case 'extendedLine':
            return 'Extended Line'
        case 'trendAngle':
            return 'Trend Angle'
        case 'infoLine':
            return 'Info Line'
        default: {
            const spaced = drawingType.replace(/([A-Z])/g, ' $1').trim()
            return spaced.charAt(0).toUpperCase() + spaced.slice(1)
        }
    }
}

/** Convert alert drawing timestamp (UTC ms) back to chart time for rendering. */
export function alertTimestampMsToChartTime(timestampMs: number, timeZone: TimeZone): Time {
    return convertToTimeZone(timestampMs / 1000, timeZone) as Time
}

export function buildDrawingFromChartPoints(
    drawingId: string,
    drawingType: string,
    points: Point[],
    timeZone: TimeZone,
    options: Record<string, string> = {}
): AlertDrawing {
    return {
        drawingType,
        drawingId,
        points: points.map((point) => ({
            timestamp: chartTimeToAlertTimestampMs(point.x, timeZone),
            price: point.y,
        })),
        options,
    }
}

export function getDrawingPriceAtTimestamp(drawing: AlertDrawing, timestampMs: number): number {
    const points = drawing.points ?? []
    if (!points.length) return 0
    const drawingType = drawing.drawingType ?? ''
    if (drawingType === 'horizontalLine' || drawingType === 'horizontalRay') {
        return points[0]?.price ?? 0
    }
    const p1 = points[0]
    const p2 = points[1] ?? p1
    const t1 = Number(p1.timestamp ?? 0)
    const t2 = Number(p2.timestamp ?? 0)
    const price1 = p1.price ?? 0
    const price2 = p2.price ?? 0
    if (t2 === t1) return price1
    const slope = (price2 - price1) / (t2 - t1)
    return price1 + slope * (timestampMs - t1)
}

export function isDrawingAlert(alert: UserAlert): boolean {
    const sac = alert.condition?.securityAlertCondition
    return Boolean(
        sac &&
        !alert.condition?.compositeCondition &&
        sac.rhsExpression?.type === AlertExpression_Type.DRAWING &&
        (sac.rhsExpression.drawing?.points?.length ?? 0) > 0
    )
}

export function getDrawingFromAlert(alert: UserAlert): AlertDrawing | undefined {
    if (!isDrawingAlert(alert)) return undefined
    return alert.condition!.securityAlertCondition!.rhsExpression!.drawing
}

/**
 * If an alertable drawing is focused on the chart, build its AlertDrawing payload
 * so Create Alert can offer it in the RHS dropdown (TradingView-style).
 */
export function getFocusedAlertableSourceDrawing(chart: {
    drawingManager: { getFocusedDrawing: () => string | undefined }
    getDrawingConfig: (id: string) => { type?: string; options?: { drawingType?: string } } | undefined | void
    getSettingsValues: (id: string) => { point1?: Point; point2?: Point } | undefined
    timezone: TimeZone
}): AlertDrawing | undefined {
    const drawingId = chart.drawingManager.getFocusedDrawing()
    if (!drawingId) return undefined

    const config = chart.getDrawingConfig(drawingId) as
        | { type?: string; options?: { drawingType?: string } }
        | undefined
        | null
    if (!config) return undefined
    const drawingType = (config.type ?? config.options?.drawingType) as string | undefined
    if (!isAlertableDrawingType(drawingType)) return undefined

    const settings = chart.getSettingsValues(drawingId)
    const point1 = settings?.point1
    const point2 = settings?.point2
    const isHorizontal = isHorizontalAlertDrawingType(drawingType)

    if (point1?.y == null) return undefined
    if (isHorizontal) {
        if (point1.x == null) return undefined
        return buildDrawingFromChartPoints(drawingId, drawingType, [point1], chart.timezone)
    }
    if (point1.x == null || point2?.x == null || point2?.y == null) return undefined
    return buildDrawingFromChartPoints(drawingId, drawingType, [point1, point2], chart.timezone)
}

/** @deprecated Use isDrawingAlert */
export const isTrendlineAlert = isDrawingAlert

/** @deprecated Use getDrawingFromAlert */
export const getTrendlineFromAlert = getDrawingFromAlert

/** Alerts rendered on chart: horizontal price levels or drawing-based crossings. */
export function isChartOverlayAlert(alert: UserAlert): boolean {
    return isDirectSecurityPriceAlert(alert) || isDrawingAlert(alert)
}

export function createUserAlertFromDrawing(
    chartSnapshot: ChartAlertSnapshot,
    drawing: AlertDrawing
): UserAlert {
    const base = createUserAlertFromSnapshot(chartSnapshot)
    const sac = base.condition?.securityAlertCondition
    if (!sac) return base
    const rhsExpression: AlertExpression = {
        type: AlertExpression_Type.DRAWING,
        drawing,
        timeBucket: undefined,
        simpleSecurity: undefined,
        spreadSecurity: undefined,
        indicator: undefined,
        fixedValue: undefined,
    }
    return {
        ...base,
        condition: {
            ...base.condition,
            securityAlertCondition: {
                ...sac,
                rhsExpression,
            },
        },
    }
}

/** @deprecated Use createUserAlertFromDrawing */
export function createUserAlertFromTrendlineDrawing(
    chartSnapshot: ChartAlertSnapshot,
    drawing: AlertDrawing
): UserAlert {
    return createUserAlertFromDrawing(chartSnapshot, drawing)
}

/** @deprecated Use buildDrawingFromChartPoints */
export function buildTrendlineFromDrawingPoints(
    drawingId: string,
    point1: Point,
    point2: Point,
    timeZone: TimeZone
): AlertDrawing {
    return buildDrawingFromChartPoints(drawingId, 'trendLine', [point1, point2], timeZone)
}

/**
 * Numeric threshold for a single security-alert row (LHS vs fixed RHS) shown on the chart:
 * simple-security price or volume vs a fixed level, or technical indicator vs a fixed level.
 */
export function getPrimaryChartAlertThresholdPrice(alert: UserAlert): number | undefined {
    const sac = alert.condition?.securityAlertCondition
    if (!sac || alert.condition?.compositeCondition) return undefined
    if (!sac.rhsExpression || sac.rhsExpression.type !== AlertExpression_Type.FIXED_VALUE) return undefined
    const v = sac.rhsExpression.fixedValue
    if (v === undefined || v === null || Number.isNaN(Number(v))) return undefined
    const lhs = sac.lhsExpression
    if (!lhs) return undefined
    if (lhs.type === AlertExpression_Type.SIMPLE_SECURITY && lhs.simpleSecurity?.security) {
        const src = lhs.simpleSecurity.source || 'close'
        // if (src === FixedExpressionOutputTypes.VOLUME.toString()) return undefined
        return Number(v)
    }
    if (lhs.type === AlertExpression_Type.SPREAD_SECURITY && lhs.spreadSecurity) {
        return Number(v)
    }
    if (lhs.type === AlertExpression_Type.TECHNICAL_INDICATOR && lhs.indicator) {
        return Number(v)
    }
    return undefined
}

/** True for a single-row condition with a fixed RHS level draggable on chart (security or indicator LHS). */
export function isDirectSecurityPriceAlert(alert: UserAlert): boolean {
    return Boolean(
        alert.condition?.securityAlertCondition &&
        !alert.condition?.compositeCondition &&
        getPrimaryChartAlertThresholdPrice(alert) !== undefined
    )
}

export function filterAlertsForChartPriceLines(
    alerts: UserAlert[],
    primarySecurity: Security | undefined,
    expressionMode: boolean,
    comparableSecurities: Security[] = [],
    chartExpression: string = '',
    onlyActiveAlerts = true
): UserAlert[] {
    const passesStatusFilter = (status: UserAlert_Status): boolean => {
        if (status === UserAlert_Status.DELETED) return false
        if (onlyActiveAlerts) return status === UserAlert_Status.ACTIVE
        return true
    }
    const normalizedChartExpression = chartExpression.trim()
    if (expressionMode) {
        return alerts.filter((a) => {
            if (!passesStatusFilter(a.status)) return false
            if (a.type === UserAlert_Type.SCREENER) return false
            if (!isChartOverlayAlert(a)) return false
            const [, alertExpression] = getUserAlertUnderlyingSecurity(a)
            if (!alertExpression) return false
            return normalizedChartExpression.length > 0 && alertExpression.trim() === normalizedChartExpression
        })
    }
    if (!primarySecurity) return []
    const pk = getSecurityKey(primarySecurity.exchange, primarySecurity.code)
    const comparableKeys = new Set(
        comparableSecurities.map((s) => getSecurityKey(s.exchange, s.code))
    )
    return alerts.filter((a) => {
        if (!passesStatusFilter(a.status)) return false
        if (a.type === UserAlert_Type.SCREENER) return false
        if (!isChartOverlayAlert(a)) return false
        const matchBySecurityKey = (key: string | undefined): boolean =>
            Boolean(key && (key === pk || comparableKeys.has(key)))

        if (hasUsableTopLevelAlertSecurity(a.security)) {
            const topLevelKey = getSecurityKey(a.security!.exchange, a.security!.code)
            if (matchBySecurityKey(topLevelKey)) return true
        }
        const [underlyingSecurities] = getUserAlertUnderlyingSecurity(a)
        return underlyingSecurities.some((sec) => sec?.code && matchBySecurityKey(getSecurityKey(sec.exchange, sec.code)))
    })
}

/**
 * Updates the fixed RHS price on a security alert. Optionally regenerates `message` the same way as the alert dialog
 * (`getUserAlertMessageFromCondition`), using `symbolDisplayName` for spread / indicator tails (underlying uses
 * `formatSecurityLabelForAlert` on LHS securities).
 */
export async function userAlertWithUpdatedRhsFixedPrice(
    alert: UserAlert,
    newPrice: number,
    opts?: {
        regenerateMessage?: boolean
        indicators?: Indicator[]
        /** Prefer trading symbol (e.g. from alert.security or LHS); same role as alert dialog `symbolDisplayName`. */
        symbolDisplayName?: string
        chartSnapshot?: ChartAlertSnapshot
        chartTimeBucket?: TimeBucket
    }
): Promise<UserAlert> {
    if (!alert.condition?.securityAlertCondition) return alert
    const sac = alert.condition.securityAlertCondition
    if (!sac.rhsExpression || sac.rhsExpression.type !== AlertExpression_Type.FIXED_VALUE) return alert
    const nextSac = {
        ...sac,
        rhsExpression: {
            ...sac.rhsExpression,
            fixedValue: newPrice
        }
    }
    const nextCondition: Condition = {
        ...alert.condition,
        securityAlertCondition: nextSac
    }
    const nextAlert: UserAlert = {
        ...alert,
        condition: nextCondition
    }
    if (opts?.regenerateMessage && opts.indicators) {
        const sym = opts.symbolDisplayName ?? ''
        const generated = (await getUserAlertMessageFromCondition(nextCondition, sym, opts.indicators, {
            chartSnapshot: opts.chartSnapshot,
            chartTimeBucket: opts.chartTimeBucket ?? alert.timeBucket,
        })).trim()
        if (generated) {
            return { ...nextAlert, message: generated }
        }
    }
    return nextAlert
}

/** Symbol (or code fallback) for alert message generation; prefers `alert.security`, then LHS underlying. */
export function getAlertMessageSymbolDisplayName(alert: UserAlert): string {
    const top = alert.security as (typeof alert.security & { symbol?: string }) | undefined
    if (top?.code) {
        const sym = top.symbol?.trim()
        if (sym) return sym
        return top.code
    }
    const [secs] = getUserAlertUnderlyingSecurity(alert)
    const s0 = secs[0]
    if (!s0?.code) return ''
    const sym = s0.symbol?.trim()
    return sym || s0.code
}

/** Runtime indicator / volume series groups on the chart used to place alert overlays. */
export type ChartAlertSeriesPlacement = {
    paneId: string
    scriptCode: string
    inputValues: Record<string, string>
    series: { id: string; code: string }[]
}

/** Compare-security line series on the chart (overlay or separate pane) for alert overlay placement. */
export type ChartAlertComparablePlacement = {
    paneId: string
    seriesId: string
    exchange: Exchange
    code: string
}

/** Alert params must match the chart for every key the alert stores; chart may have extra defaulted inputs. */
function indicatorInputValuesEqualForAlertIndicator(
    alertIndicator: AlertIndicator | undefined,
    chartInputValues: Record<string, string>,
    matchContext?: AlertIndicatorMatchContext
): boolean {
    if (!alertIndicator) return false
    const flatParams = flattenAlertIndicatorParamsForChart(
        alertIndicator,
        undefined,
        matchContext?.indicators ?? [],
        matchContext?.seriesGroups
    )
    return indicatorInputValuesSemanticallyEqual(
        flatParams,
        chartInputValues,
        matchContext?.seriesGroups,
        matchContext?.indicators
    )
}

/** Alert params must match the chart for every key the alert stores; chart may have extra defaulted inputs. */
function indicatorInputValuesEqualOnChart(
    params: Params[] | undefined,
    chartInputValues: Record<string, string>,
    matchContext?: AlertIndicatorMatchContext
): boolean {
    const alertNorm: Record<string, string> = {}
    for (const p of params ?? []) {
        if (!p.key) continue
        alertNorm[sanitizeId(p.key)] = String(p.value ?? '')
    }
    return indicatorInputValuesSemanticallyEqual(
        alertNorm,
        chartInputValues,
        matchContext?.seriesGroups,
        matchContext?.indicators
    )
}

/** LHS (or alert-level) time bucket must match the chart aggregation for indicator overlays. */
function alertChartTimeBucketsMatchForIndicator(
    alertTb: TimeBucket | undefined,
    chartTb: TimeBucket | undefined
): boolean {
    if (chartTb === undefined) return alertTb === undefined
    // No explicit timeframe on the alert → treat as "current chart" (common for saved/API alerts).
    if (alertTb === undefined) return true
    return encodeAlertExpressionTimeBucketKey(alertTb) === encodeAlertExpressionTimeBucketKey(chartTb)
}

function isVolumeLhsSource(source: string | undefined): boolean {
    if (!source) return false
    const normalized = String(source).trim().toLowerCase()
    return (
        normalized === String(FixedExpressionOutputTypes.VOLUME).trim().toLowerCase() ||
        normalized === String(FixedExpressionOutputTypes.VOLUME.toString()).trim().toLowerCase()
    )
}

/** True when the alert's draggable level is volume (simple- or spread-security LHS), not price. */
export function isUserAlertVolumeLevelThreshold(alert: UserAlert | undefined): boolean {
    if (!alert?.condition?.securityAlertCondition?.lhsExpression) return false
    const lhs = alert.condition.securityAlertCondition.lhsExpression
    if (lhs.type === AlertExpression_Type.SIMPLE_SECURITY && lhs.simpleSecurity?.security) {
        return isVolumeLhsSource(lhs.simpleSecurity.source || 'close')
    }
    if (lhs.type === AlertExpression_Type.SPREAD_SECURITY) {
        return isVolumeLhsSource(lhs.spreadSecurity?.source)
    }
    return false
}

function resolveVolumeSeriesPlacement(
    placements: ChartAlertSeriesPlacement[]
): { paneId: string; seriesId: string } | null {
    const match = placements.find((p) => p.scriptCode === VOLUME_INDICATOR_CODE)
    const first = match?.series[0]
    if (!match || !first) return null
    return { paneId: match.paneId, seriesId: first.id }
}

/**
 * Pane + series for the overlay. Simple-security LHS → compare pane/series when LHS matches a comparable
 * security, else primary (volume LHS → volume scale series).
 * Technical-indicator LHS → matching indicator pane/series only when inputs and time bucket match the chart; otherwise `null` (do not draw on that pane / do not misplace).
 */
export function resolveChartAlertSeriesTarget(
    alert: UserAlert,
    primary: { paneId: string; seriesId: string },
    placements: ChartAlertSeriesPlacement[],
    chartTimeBucket: TimeBucket | undefined,
    comparablePlacements: ChartAlertComparablePlacement[] = [],
    matchContext?: AlertIndicatorMatchContext
): { paneId: string; seriesId: string } | null {
    const lhs = getPrimarySecurityAlertCondition(alert.condition)?.lhsExpression
    if (!lhs) {
        return primary
    }
    if (lhs.type === AlertExpression_Type.SIMPLE_SECURITY && lhs.simpleSecurity?.security) {
        const src = lhs.simpleSecurity.source || 'close'
        if (isVolumeLhsSource(src)) {
            return resolveVolumeSeriesPlacement(placements)
        }
        const sec = lhs.simpleSecurity.security
        const lhsKey = getSecurityKey(sec.exchange, sec.code)
        const comp = comparablePlacements.find((c) => getSecurityKey(c.exchange, c.code) === lhsKey)
        if (comp) return { paneId: comp.paneId, seriesId: comp.seriesId }
        return primary
    }
    if (lhs.type === AlertExpression_Type.SPREAD_SECURITY && isVolumeLhsSource(lhs.spreadSecurity?.source)) {
        return resolveVolumeSeriesPlacement(placements)
    }
    if (lhs.type !== AlertExpression_Type.TECHNICAL_INDICATOR || !lhs.indicator) {
        return primary
    }
    const ind = lhs.indicator
    if (!ind.indicatorCode || !ind.output) return null
    const alertTb = lhs.timeBucket ?? alert.timeBucket
    if (!alertChartTimeBucketsMatchForIndicator(alertTb, chartTimeBucket)) {
        return null
    }
    const match = placements.find(
        (p) =>
            p.scriptCode === ind.indicatorCode &&
            indicatorInputValuesEqualForAlertIndicator(ind as AlertIndicator, p.inputValues, matchContext) &&
            p.series.some((s) => s.code === ind.output)
    )
    if (!match) return null
    const row = match.series.find((s) => s.code === ind.output)
    if (!row) return null
    return { paneId: match.paneId, seriesId: row.id }
}

const chartAlertPlacementKeySep = '\x1f'

function chartAlertPlacementKey(paneId: string, seriesId: string): string {
    return `${paneId}${chartAlertPlacementKeySep}${seriesId}`
}

function encodeAlertIndicatorParamsForPartition(
    alertIndicator: AlertIndicator | undefined,
    matchContext?: AlertIndicatorMatchContext
): string {
    let paramsMap = flattenAlertIndicatorParamsForChart(
        alertIndicator ?? { indicatorCode: '', params: [], output: '', expression: undefined },
        undefined,
        matchContext?.indicators ?? [],
        matchContext?.seriesGroups
    )
    const entries = Object.entries(paramsMap).map(([k, v]) => [sanitizeId(k), String(v ?? '')] as const)
    entries.sort((a, b) => a[0].localeCompare(b[0]))
    return entries.map(([k, v]) => `${k}=${v}`).join('&')
}

/** Same pane + indicator + params + output → one overlay even if the chart has duplicate series instances. */
function chartAlertIndicatorLogicalPartitionKey(
    paneId: string,
    alertIndicator: AlertIndicator,
    matchContext?: AlertIndicatorMatchContext
): string {
    return `ind${chartAlertPlacementKeySep}${paneId}${chartAlertPlacementKeySep}${alertIndicator.indicatorCode}${chartAlertPlacementKeySep}${encodeAlertIndicatorParamsForPartition(alertIndicator, matchContext)}${chartAlertPlacementKeySep}${alertIndicator.output}`
}

/** Same pane + compare symbol → one overlay even if multiple comparable series exist for that symbol. */
function chartAlertCompareLogicalPartitionKey(paneId: string, lhsSecurityKey: string): string {
    return `cmp${chartAlertPlacementKeySep}${paneId}${chartAlertPlacementKeySep}${lhsSecurityKey}`
}

function findAllChartAlertIndicatorPlacements(
    alert: UserAlert,
    placements: ChartAlertSeriesPlacement[],
    chartTimeBucket: TimeBucket | undefined,
    matchContext?: AlertIndicatorMatchContext
): { paneId: string; seriesId: string }[] {
    const lhs = getPrimarySecurityAlertCondition(alert.condition)?.lhsExpression
    if (lhs?.type !== AlertExpression_Type.TECHNICAL_INDICATOR || !lhs.indicator) return []
    const ind = lhs.indicator
    if (!ind.indicatorCode || !ind.output) return []
    const alertTb = lhs.timeBucket ?? alert.timeBucket
    if (!alertChartTimeBucketsMatchForIndicator(alertTb, chartTimeBucket)) return []
    const out: { paneId: string; seriesId: string }[] = []
    const seenLogical = new Set<string>()
    for (const p of placements) {
        if (
            p.scriptCode === ind.indicatorCode &&
            indicatorInputValuesEqualForAlertIndicator(ind as AlertIndicator, p.inputValues, matchContext) &&
            p.series.some((s) => s.code === ind.output)
        ) {
            const row = p.series.find((s) => s.code === ind.output)
            if (!row) continue
            const logical = chartAlertIndicatorLogicalPartitionKey(
                p.paneId,
                ind as AlertIndicator,
                matchContext
            )
            if (seenLogical.has(logical)) continue
            seenLogical.add(logical)
            out.push({ paneId: p.paneId, seriesId: row.id })
        }
    }
    return out
}

function findAllChartAlertComparePlacements(
    lhsKey: string,
    comparablePlacements: ChartAlertComparablePlacement[]
): { paneId: string; seriesId: string }[] {
    const out: { paneId: string; seriesId: string }[] = []
    const seenLogical = new Set<string>()
    for (const c of comparablePlacements) {
        if (getSecurityKey(c.exchange, c.code) !== lhsKey) continue
        const logical = chartAlertCompareLogicalPartitionKey(c.paneId, lhsKey)
        if (seenLogical.has(logical)) continue
        seenLogical.add(logical)
        out.push({ paneId: c.paneId, seriesId: c.seriesId })
    }
    return out
}

function appendAlertToPartitionBucket(
    map: Map<string, { paneId: string; seriesId: string; alerts: UserAlert[] }>,
    key: string,
    paneId: string,
    seriesId: string,
    alert: UserAlert
) {
    let bucket = map.get(key)
    if (!bucket) {
        bucket = { paneId, seriesId, alerts: [] }
        map.set(key, bucket)
    }
    if (!bucket.alerts.some((a) => a.id === alert.id)) {
        bucket.alerts.push(alert)
    }
}

/** Group alerts by the pane/series whose scale should draw the threshold line. */
export function partitionChartAlertsBySeriesTarget(
    alerts: UserAlert[],
    primary: { paneId: string; seriesId: string } | undefined,
    placements: ChartAlertSeriesPlacement[],
    chartTimeBucket: TimeBucket | undefined,
    comparablePlacements: ChartAlertComparablePlacement[] = [],
    matchContext?: AlertIndicatorMatchContext
): Map<string, { paneId: string; seriesId: string; alerts: UserAlert[] }> {
    const map = new Map<string, { paneId: string; seriesId: string; alerts: UserAlert[] }>()
    if (!primary) return map
    for (const alert of alerts) {
        if (!isDirectSecurityPriceAlert(alert)) continue
        const sac = getPrimarySecurityAlertCondition(alert.condition)
        const lhs = sac?.lhsExpression

        if (lhs?.type === AlertExpression_Type.TECHNICAL_INDICATOR && lhs.indicator?.indicatorCode && lhs.indicator.output) {
            const targets = findAllChartAlertIndicatorPlacements(alert, placements, chartTimeBucket, matchContext)
            for (const t of targets) {
                const key = chartAlertIndicatorLogicalPartitionKey(
                    t.paneId,
                    lhs.indicator as AlertIndicator,
                    matchContext
                )
                appendAlertToPartitionBucket(map, key, t.paneId, t.seriesId, alert)
            }
            continue
        }

        if (
            lhs?.type === AlertExpression_Type.SIMPLE_SECURITY &&
            lhs.simpleSecurity?.security &&
            !isVolumeLhsSource(lhs.simpleSecurity.source || 'close')
        ) {
            const sec = lhs.simpleSecurity.security
            const lhsKey = getSecurityKey(sec.exchange, sec.code)
            const compareTargets = findAllChartAlertComparePlacements(lhsKey, comparablePlacements)
            if (compareTargets.length > 0) {
                for (const t of compareTargets) {
                    const key = chartAlertCompareLogicalPartitionKey(t.paneId, lhsKey)
                    appendAlertToPartitionBucket(map, key, t.paneId, t.seriesId, alert)
                }
                continue
            }
        }

        const t = resolveChartAlertSeriesTarget(
            alert,
            primary,
            placements,
            chartTimeBucket,
            comparablePlacements,
            matchContext
        )
        if (t === null) continue
        const key = chartAlertPlacementKey(t.paneId, t.seriesId)
        appendAlertToPartitionBucket(map, key, t.paneId, t.seriesId, alert)
    }
    return map
}